import { useEffect, useMemo, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import type {
  AvvisoAzione, AzioneInChat, CampoCambiato, CartaConferma, EsitoAnnulla, EsitoCarta, ParteRisposta, RigaCarta, RigaLink,
} from '@masamune/shared';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAssistenteStore, type MessaggioChat } from '../../stores/assistente.store';
import { annullaAzione } from './assistenteApi';
import { leggiConversazione, testoDaLeggere } from './chatApi';
import { festeggiaAzione } from './mascotteVita';
import { oraDiRoma, rinfrescaDopoAnnulla, rinfrescaSpesa, testoAvviso } from './registroApi';

/**
 * MUNE che fa (29/10/2026, fetta 2D «Fa» della Fase 2, os-planning/ai/fase-2-progetto.md,
 * «Interfaccia» e «Contratti»): la carta «Modifiche da confermare» e la riga «Fatto … ·
 * Annulla» nella risposta della chat. Qui le chiamate, lo stato condiviso e le parole; i
 * componenti stanno in CartaConferma.tsx e AzioneInChat.tsx, che non esportano costanti
 * (un .tsx che esporta anche funzioni rompe il Fast Refresh: schermo bianco).
 *
 * - Una scrittura fatta subito arriva come parte `azione`: «Annulla» la rimette com'era
 *   (POST /ai/azioni/:id/annulla).
 * - Una richiesta che cancella, manda mail o tocca più cose arriva come parte `carta`: una
 *   riga per modifica con la spunta, «Fai le 4 modifiche» (POST /ai/azioni/:id/conferma con
 *   gli indici spuntati) o «Non fare le modifiche» (POST /ai/azioni/:id/rifiuta). Fatte, ogni
 *   riga ha «Annulla questa modifica» (l'azione figlia) e in fondo «Annulla tutto» (la carta).
 *
 * L'esito di ogni clic aggiorna la parte nel messaggio della chat (assistente.store), così la
 * risposta resta com'è anche se il pannello si rimonta; riaprendo la conversazione lo stato
 * di adesso lo dà il server. Le spunte, i clic in corso e gli Annulla fatti stanno in uno
 * store a parte (useCarteStore): al telefono la carta sta nella chat e i suoi tasti nella barra
 * in fondo al pannello, e devono vedere le stesse spunte. Le chiamate partono solo dai clic.
 */

// ── Le rotte (Contratti, 2D) ─────────────────────────────────────────────────

/** «Fai le 4 modifiche»: gli indici delle righe con la spunta. */
export function confermaCarta(id: string, righe: number[]): Promise<EsitoCarta> {
  return api.post<EsitoCarta>(`/ai/azioni/${encodeURIComponent(id)}/conferma`, { righe });
}

/** «Non fare le modifiche»: la carta diventa RIFIUTATA e non scrive niente. */
export function rifiutaCarta(id: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/ai/azioni/${encodeURIComponent(id)}/rifiuta`);
}

/**
 * Dopo una modifica di MUNE, confermata o annullata, si rileggono le pagine che può aver
 * toccato: task, note del cliente, Diario, trattative, schede e registro
 * (rinfrescaDopoAnnulla, allargata per la 2D), più lo stato di MUNE e la spesa, così
 * l'intestazione si accorge di «Solo lettura» o di MUNE spento. Le query che nessuna pagina
 * sta mostrando si segnano soltanto: si rileggono quando si riaprono.
 */
export function rinfrescaDopoModifiche(qc: QueryClient) {
  rinfrescaDopoAnnulla(qc);
  rinfrescaSpesa(qc);
}

// ── Lo stato condiviso: spunte, clic in corso, Annulla fatti ─────────────────

/** Il clic in corso su una carta o su un'azione. */
export type Lavoro = 'conferma' | 'rifiuta' | 'annulla';

/**
 * Com'è andato l'Annulla di un'azione della chat, di una riga della carta o della carta
 * intera: ANNULLATA e PARZIALE dall'esito del server, NIENTE dal 409 che dice perché non si
 * annulla più («Non si annulla più: Andrea l'ha cambiata il 29/10»).
 */
export interface Annullo {
  esito: 'ANNULLATA' | 'PARZIALE' | 'NIENTE';
  frase: string;
}

interface CarteState {
  /** Per carta, le righe a cui è stata tolta la spunta: di base sono tutte spuntate. */
  tolte: Record<string, readonly number[]>;
  /** Il clic in corso, per carta o per azione: un clic alla volta, anche tra carta e barra. */
  lavori: Record<string, Lavoro>;
  /** La frase dell'ultimo errore di una carta ancora da decidere (Solo lettura, rete). */
  problemi: Record<string, string>;
  /** Carte che non si possono più usare (già usata, di un'altra persona): la frase del server. */
  chiuse: Record<string, string>;
  /** Gli Annulla fatti da qui, per id dell'azione o della carta. */
  annulli: Record<string, Annullo>;
  /**
   * Le task create da MUNE e tornate nel cestino con l'Annulla (29/10/2026, dalla revisione della
   * 2D), e dalla revisione delle correzioni quelle eliminate con la carta: le loro righe col link
   * escono dalla chat, anche quelle che arrivano dopo, a fine turno. Un'eliminazione annullata le
   * toglie da qui (ripesca).
   */
  cestinate: Record<string, true>;
  spunta: (cartaId: string, indice: number, on: boolean) => void;
  /** Segna il clic; false se ce n'è già uno in corso sullo stesso id. */
  inizia: (id: string, lavoro: Lavoro) => boolean;
  finisci: (id: string) => void;
  problema: (id: string, frase: string | null) => void;
  chiudi: (id: string, frase: string) => void;
  annullata: (id: string, a: Annullo) => void;
  cestina: (ids: readonly string[]) => void;
  ripesca: (ids: readonly string[]) => void;
}

function senza<T>(r: Record<string, T>, id: string): Record<string, T> {
  if (!(id in r)) return r;
  const copia = { ...r };
  delete copia[id];
  return copia;
}

export const useCarteStore = create<CarteState>()((set, get) => ({
  tolte: {},
  lavori: {},
  problemi: {},
  chiuse: {},
  annulli: {},
  cestinate: {},
  spunta: (cartaId, indice, on) =>
    set((s) => {
      const prima = s.tolte[cartaId] ?? [];
      const dopo = on ? prima.filter((i) => i !== indice) : prima.includes(indice) ? prima : [...prima, indice];
      return { tolte: { ...s.tolte, [cartaId]: dopo } };
    }),
  inizia: (id, lavoro) => {
    if (get().lavori[id]) return false;
    set((s) => ({ lavori: { ...s.lavori, [id]: lavoro }, problemi: senza(s.problemi, id) }));
    return true;
  },
  finisci: (id) => set((s) => ({ lavori: senza(s.lavori, id) })),
  problema: (id, frase) => set((s) => ({ problemi: frase ? { ...s.problemi, [id]: frase } : senza(s.problemi, id) })),
  chiudi: (id, frase) => set((s) => ({ chiuse: { ...s.chiuse, [id]: frase } })),
  annullata: (id, a) => set((s) => ({ annulli: { ...s.annulli, [id]: a } })),
  cestina: (ids) => set((s) => ({ cestinate: { ...s.cestinate, ...Object.fromEntries(ids.map((id) => [id, true as const])) } })),
  ripesca: (ids) => set((s) => ({ cestinate: Object.fromEntries(Object.entries(s.cestinate).filter(([id]) => !ids.includes(id))) as Record<string, true> })),
}));

const NESSUNA: readonly number[] = [];

// ── La parte nel messaggio della chat ────────────────────────────────────────

/** Cambia le parti che corrispondono, in ogni messaggio della chat che ne ha una. */
function cambiaParti(trova: (p: ParteRisposta) => boolean, cambia: (p: ParteRisposta) => ParteRisposta) {
  const st = useAssistenteStore.getState();
  for (const m of st.messaggi) {
    const parti = m.parti;
    if (!parti || !parti.some(trova)) continue;
    st.aggiornaMessaggio(m.id, { parti: parti.map((p) => (trova(p) ? cambia(p) : p)) });
  }
}

/** La carta con questo id, nella chat aperta, diventa `cambia(carta)`. Fuori dalla chat non fa niente. */
export function aggiornaCarta(id: string, cambia: (c: CartaConferma) => CartaConferma) {
  cambiaParti(
    (p) => p.tipo === 'carta' && p.carta?.id === id,
    (p) => (p.tipo === 'carta' ? { tipo: 'carta', carta: cambia(p.carta) } : p),
  );
}

/** L'azione con questo id, nella chat aperta, diventa `cambia(azione)`. */
export function aggiornaAzione(id: string, cambia: (a: AzioneInChat) => AzioneInChat) {
  cambiaParti(
    (p) => p.tipo === 'azione' && p.azione?.id === id,
    (p) => (p.tipo === 'azione' ? { tipo: 'azione', azione: cambia(p.azione) } : p),
  );
}

/** Quante volte, e ogni quanto, si rilegge una carta confermata che non ha ancora l'esito. */
const RILETTURE_SENZA_ESITO = 5;
const PAUSA_RILETTURA_MS = 1500;

/**
 * La carta com'è adesso sul server, presa dalla conversazione salvata (GET
 * /ai/conversazioni/:id dà carte e azioni col loro stato di adesso). Serve quando il server
 * dice che la proposta è già stata usata (un altro clic, il telefono) o dopo un «Annulla
 * tutto» riuscito in parte: sa lui quali righe sono tornate com'erano. Se la conversazione
 * nel frattempo è cambiata, o la lettura non va, la carta resta com'è.
 *
 * Una carta confermata e ancora senza esito (l'altro clic sta ancora facendo le righe: l'esito si
 * salva alla fine) si rilegge qualche altra volta, finché l'esito arriva (dalla revisione delle
 * correzioni, 29/10/2026): prima restava «Modifiche non fatte» con le modifiche fatte.
 */
export async function rileggiCarta(id: string): Promise<void> {
  const conversazione = useAssistenteStore.getState().conversazioneId;
  if (!conversazione) return;
  for (let volta = 0; volta <= RILETTURE_SENZA_ESITO; volta++) {
    if (volta) {
      await new Promise((ok) => setTimeout(ok, PAUSA_RILETTURA_MS));
      if (useAssistenteStore.getState().conversazioneId !== conversazione) return;
    }
    let carta: CartaConferma | null = null;
    try {
      const salvata = await leggiConversazione(conversazione);
      carta = salvata.turni.flatMap((t) => t.risposta?.parti ?? []).flatMap((p) => (p.tipo === 'carta' && p.carta?.id === id ? [p.carta] : []))[0] ?? null;
    } catch {
      // Resta quello che si vede: la frase del server l'ha già detto.
      return;
    }
    if (!carta || useAssistenteStore.getState().conversazioneId !== conversazione) return;
    const trovata = carta;
    aggiornaCarta(id, () => trovata);
    if (trovata.stato !== 'ESEGUITA' || trovata.esito) return;
  }
}

// ── La forma, anche di una conversazione di settimane fa ─────────────────────

const STATI_CARTA: ReadonlySet<string> = new Set(['PROPOSTA', 'ESEGUITA', 'RIFIUTATA', 'SCADUTA']);

const testo = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Una carta salvata in AiMessage.shown si rilegge com'era quando è nata: un campo che allora
 * non c'era diventa vuoto invece di rompere la conversazione riaperta, come fa
 * riquadroCompleto per i riquadri. I campi in più del server restano.
 */
export function cartaCompleta(c: CartaConferma): CartaConferma {
  const righe: RigaCarta[] = Array.isArray(c.righe)
    ? c.righe
      .filter((r) => r && Number.isInteger(r.indice))
      .map((r) => ({
        ...r,
        cosa: testo(r.cosa),
        campi: Array.isArray(r.campi) ? r.campi.filter((x): x is CampoCambiato => !!x && typeof x.campo === 'string') : [],
        avvisi: Array.isArray(r.avvisi) ? r.avvisi.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [],
        descrizioneMail: typeof r.descrizioneMail === 'string' && r.descrizioneMail.trim() ? r.descrizioneMail : null,
        eliminazione: !!r.eliminazione,
      }))
    : [];
  const esito: EsitoCarta | null = c.esito && Array.isArray(c.esito.righe)
    ? {
      ...c.esito,
      frase: testo(c.esito.frase),
      righe: c.esito.righe
        .filter((r) => r && Number.isInteger(r.indice))
        .map((r) => ({
          ...r,
          frase: testo(r.frase),
          azioneId: typeof r.azioneId === 'string' && r.azioneId ? r.azioneId : null,
          avvisi: avvisiValidi(r.avvisi),
          perche: typeof r.perche === 'string' && r.perche.trim() ? r.perche : null,
        })),
    }
    : null;
  return {
    ...c,
    titolo: testo(c.titolo),
    scade: testo(c.scade),
    righe,
    tasto: testo(c.tasto),
    stato: STATI_CARTA.has(c.stato) ? c.stato : 'SCADUTA',
    esito,
  };
}

function avvisiValidi(v: unknown): AvvisoAzione[] {
  return Array.isArray(v) ? v.filter((x): x is AvvisoAzione => !!x && typeof x.canale === 'string' && typeof x.cosa === 'string') : [];
}

/** Lo stesso per un'azione della chat. */
export function azioneCompleta(a: AzioneInChat): AzioneInChat {
  return {
    ...a,
    frase: testo(a.frase),
    avvisi: avvisiValidi(a.avvisi),
    annullabile: !!a.annullabile,
    stato: a.stato === 'ANNULLATA' ? 'ANNULLATA' : 'ESEGUITA',
    perche: typeof a.perche === 'string' && a.perche.trim() ? a.perche : null,
  };
}

// ── Le parole (os-planning/ai/fase-2-progetto.md, «Parole a schermo») ────────

/** La rete che cade: la stessa frase del pannello per una domanda che non parte. */
const FRASE_RETE = 'MUNE non risponde: controlla la connessione e riprova';

const fraseErrore = (e: unknown) => (e instanceof ApiError ? e.message : FRASE_RETE);

/** «Andrea», «Andrea e Lorenzo», «Discord, Andrea e Lorenzo» (con «ed» davanti alla e, come fraseLette). */
function elenco(nomi: string[]): string {
  if (nomi.length < 2) return nomi[0] ?? '';
  const ultimo = nomi[nomi.length - 1]!;
  return `${nomi.slice(0, -1).join(', ')} ${/^[eE]/.test(ultimo) ? 'ed' : 'e'} ${ultimo}`;
}

/**
 * «avvisati Discord e Andrea»: chi ha ricevuto qualcosa per questa modifica. Discord per
 * nome del canale, le persone per nome (il server scrive il nome, mai l'indirizzo, e una mail
 * a più persone li ha già uniti: «Andrea e Lorenzo»). Discord prima, poi le persone nell'ordine
 * in cui sono partiti gli avvisi. Senza nessun nome resta che qualcosa è partito.
 */
export function fraseAvvisati(avvisi: AvvisoAzione[]): string | null {
  if (!avvisi.length) return null;
  const nomi: string[] = [];
  const metti = (nome: string) => {
    const n = nome.trim();
    if (n && !nomi.some((x) => x.toLocaleLowerCase('it-IT') === n.toLocaleLowerCase('it-IT'))) nomi.push(n);
  };
  if (avvisi.some((v) => v.canale === 'discord')) metti('Discord');
  for (const v of avvisi) {
    if (v.canale !== 'discord' && v.a) v.a.split(/\s*,\s*|\s+ed?\s+/).forEach(metti);
  }
  if (!nomi.length) return avvisi.length === 1 ? 'partito un avviso' : 'partiti degli avvisi';
  return `avvisati ${elenco(nomi)}`;
}

/** «avvisati Discord e Andrea» su una riga sua, al telefono: «Avvisati Discord e Andrea». */
export const conMaiuscola = (s: string) => s.charAt(0).toLocaleUpperCase('it-IT') + s.slice(1);

/** Il dettaglio degli avvisi, al passaggio del mouse: «Mail di assegnazione di "…" per Andrea». */
export function dettaglioAvvisi(avvisi: AvvisoAzione[]): string | null {
  return avvisi.length ? avvisi.map(testoAvviso).join('\n') : null;
}

/**
 * «Fatto: task "Chiamare Riccardo di Graci" per te, giovedì 29/10» annullata diventa
 * «Annullato: task "Chiamare Riccardo di Graci"» (Parole a schermo). Serve quando la frase
 * dell'Annulla del server non c'è, cioè nella conversazione riaperta. Dal 29/10/2026 (revisione
 * della 2D) tiene il verbo, che dice cosa è tornato indietro («Annullato: "Villa Fago, rinnovo
 * 2027" spostata in "Trattativa"»; prima si fermava al primo nome tra virgolette), e toglie solo
 * il giorno in coda; di una task nuova o di una prossima azione resta il titolo, come nelle
 * Parole a schermo: persona e giorno non valgono più.
 */
export function fraseAnnullata(frase: string): string {
  if (/^\s*annullat/i.test(frase)) return frase;
  const resto = frase.replace(/^\s*fatt[oaie]\b\s*[:,.]?\s*/i, '');
  const nuova = /^((?:task|prossima azione)\s+(?:"[^"]*"|«[^»]*»))/i.exec(resto)?.[1];
  const cosa = (nuova ?? resto.replace(/,\s*(?:lunedì|martedì|mercoledì|giovedì|venerdì|sabato|domenica)\s+\d{1,2}\/\d{1,2}\s*$/i, '')).trim();
  return cosa ? `Annullato: ${cosa}` : 'Annullato';
}

/**
 * La frase della riga di un'azione: com'è adesso, anche dopo l'Annulla. Annullata da qui o dal
 * registro c'è la frase del server, che dice cosa è tornato («Annullato: "Villa Fago, rinnovo
 * 2027" torna in "Proposta inviata".») e cosa resta («Il messaggio su Discord è già partito.»):
 * dal 29/10/2026 (revisione della 2D) anche per un Annulla riuscito per intero, non solo in parte.
 */
export function fraseAzione(a: AzioneInChat, annullo: Annullo | null): string {
  const annullata = annullo?.esito === 'ANNULLATA' || annullo?.esito === 'PARZIALE';
  if (annullata && annullo.frase) return annullo.frase;
  if (annullata || a.stato === 'ANNULLATA') return fraseAnnullata(a.frase);
  return a.frase;
}

/**
 * Chi è stato avvisato, anche dopo l'Annulla: mail, Discord e campanelle restano (29/10/2026,
 * dalla revisione della 2D). Mail e Discord li dice già la frase dell'Annulla del server («Il
 * messaggio su Discord è già partito.», fraseAvvisi): restano le campanelle, tranne quelle di chi
 * ha avuto anche la mail, già nominato nella frase. Dalla revisione delle correzioni (29/10/2026):
 * prima con la frase del server spariva anche chi aveva ricevuto la campanella.
 */
export function avvisatiDopo(frase: string, avvisi: AvvisoAzione[]): string | null {
  if (!/già partit/i.test(frase)) return fraseAvvisati(avvisi);
  const conMail = avvisi.filter((v) => v.canale === 'mail' && v.a).flatMap((v) => v.a!.split(/\s*,\s*|\s+ed?\s+/));
  const campanelle = avvisi.filter((v) => v.canale === 'campanella' && v.a && !conMail.includes(v.a));
  return campanelle.length ? fraseAvvisati(campanelle) : null;
}

/**
 * Il tasto pieno della carta. Con tutte le righe spuntate è quello del server («Fai le 4
 * modifiche», «Elimina le 3 task»); tolta qualche spunta, il numero si rifà dalle righe
 * scelte: un tasto che dice «4» quando se ne fanno 3 direbbe una cosa falsa (Lorenzo, 8/9:
 * i numeri a schermo si calcolano dai dati). Se restano spuntate solo task da eliminare, il
 * tasto dice «Elimina» anche con una spunta sola (29/10/2026, dalla revisione della 2D: diceva
 * «Fai la modifica» mentre il clic metteva una task nel cestino). Senza nessuna spunta il tasto
 * è spento e dice cosa manca, con la frase del server per lo stesso caso (ai.carte.ts), invece
 * del numero che non c'è più (dalla revisione delle correzioni, 29/10/2026). Parole nuove: da far
 * vedere a Lorenzo. Una carta salvata a cui cartaCompleta ha tolto tutte le righe tiene il testo
 * del server.
 */
export function testoTasto(c: CartaConferma, scelte: number[]): string {
  const n = scelte.length;
  if (n === 0 && c.righe.length) return 'Scegli almeno una modifica';
  if (c.tasto && n === c.righe.length) return c.tasto;
  if (soloEliminazioni(c, scelte)) return n === 1 ? 'Elimina la task' : `Elimina le ${n} task`;
  return n <= 1 ? 'Fai la modifica' : `Fai le ${n} modifiche`;
}

/**
 * Restano spuntate solo task da eliminare: il tasto pieno dice «Elimina» ed è rosso come gli
 * altri tasti che cancellano (btn-danger; Lorenzo, 29/10/2026: «vanno tutte bene»).
 */
export function soloEliminazioni(c: CartaConferma, scelte: number[]): boolean {
  return scelte.length > 0 && scelte.every((i) => {
    const r = c.righe.find((x) => x.indice === i);
    return !!r?.eliminazione && /^Elimina la task\b/.test(r.cosa);
  });
}

/** «Vale fino alle 11:12», in ora di Roma; niente se la scadenza non si legge. */
function valeFino(scade: string): string | null {
  return Number.isFinite(Date.parse(scade)) ? `Vale fino alle ${oraDiRoma(scade)}` : null;
}

// ── Com'è la carta adesso ────────────────────────────────────────────────────

/**
 * - aperta: da decidere, con le spunte e i tasti;
 * - scaduta: passati i 30 minuti (o lo dice il server);
 * - chiusa: il server dice che non si può più usare (già usata, di un'altra persona);
 * - fatta: confermata, con l'esito riga per riga e gli Annulla;
 * - lasciata: «Non fare le modifiche».
 */
export type FaseCarta = 'aperta' | 'scaduta' | 'chiusa' | 'fatta' | 'lasciata';

function faseDi(c: CartaConferma, chiusa: string | null, scaduta: boolean): FaseCarta {
  switch (c.stato) {
    case 'ESEGUITA': return 'fatta';
    case 'RIFIUTATA': return 'lasciata';
    case 'SCADUTA': return 'scaduta';
    default: return chiusa ? 'chiusa' : scaduta ? 'scaduta' : 'aperta';
  }
}

const TITOLO_DA_CONFERMARE = 'Modifiche da confermare';

/**
 * Il titolo della carta com'è adesso (29/10/2026, dalla revisione della 2D): «Modifiche da
 * confermare» solo finché si decide. Poi «Modifiche fatte», «Modifiche non fatte», «Modifiche
 * annullate» (tutte quelle fatte sono tornate com'erano), «Modifiche scadute», col resto del
 * titolo del server («: 2 task di Graci»). Prima restava «da confermare» anche a carta usata, e
 * l'etichetta diceva il contrario dello stato. Parole nuove: da far vedere a Lorenzo.
 *
 * Dalla revisione delle correzioni (29/10/2026): il numero si conta dalle righe nello stato che il
 * titolo nomina, e se sono meno di quelle della carta diventa «2 su 3» («Modifiche fatte: 2 su 3
 * task di Graci»; prima «Modifiche fatte: 3 task di Graci» sopra «Fatte le 2 modifiche»). Una carta
 * confermata e ancora senza esito (la conferma lavora, o l'esito non si è salvato) è «Modifiche
 * confermate», non «non fatte». Parole nuove anche queste: da far vedere a Lorenzo.
 */
function titoloDellaCarta(c: CartaConferma, fase: FaseCarta, righe: VistaRiga[]): string {
  const titolo = c.titolo || TITOLO_DA_CONFERMARE;
  if (fase === 'aperta' || fase === 'chiusa' || !titolo.startsWith(TITOLO_DA_CONFERMARE)) return titolo;
  let stato: string;
  let quante = righe.length;
  if (fase === 'scaduta') stato = 'Modifiche scadute';
  else if (fase === 'lasciata') stato = 'Modifiche non fatte';
  else if (!c.esito) stato = 'Modifiche confermate';
  else {
    const fatte = righe.filter((v) => v.segno === 'fatta').length;
    const annullate = righe.filter((v) => v.segno === 'annullata').length;
    stato = fatte ? 'Modifiche fatte' : annullate ? 'Modifiche annullate' : 'Modifiche non fatte';
    if (fatte || annullate) quante = fatte || annullate;
  }
  const resto = titolo.slice(TITOLO_DA_CONFERMARE.length);
  // «: 3 task di Graci» con 2 righe fatte diventa «: 2 su 3 task di Graci». Con una riga sola il
  // titolo dice «una task» e il conto non può scendere.
  return `${stato}${quante < righe.length ? resto.replace(/^: (\d+) /, `: ${quante} su $1 `) : resto}`;
}

/**
 * La riga sotto il titolo: per quanto vale, o com'è finita. Una carta confermata senza esito (la
 * conferma lavora ancora, o l'esito non si è salvato: le figlie stanno comunque nel registro) lo
 * dice, invece di «Fatto» (dalla revisione delle correzioni, 29/10/2026). Frase nuova: da far
 * vedere a Lorenzo.
 */
function fraseDellaCarta(c: CartaConferma, fase: FaseCarta, chiusa: string | null): string | null {
  switch (fase) {
    case 'aperta': return valeFino(c.scade);
    case 'scaduta': return 'Scaduta: chiedimelo di nuovo';
    case 'chiusa': return chiusa;
    case 'lasciata': return "Non fatte: le hai lasciate com'erano";
    case 'fatta': return c.esito ? c.esito.frase || 'Fatto' : "Confermate: com'è andata lo vedi tra poco qui e in «Cosa ha fatto MUNE»";
  }
}

type RigaEsito = EsitoCarta['righe'][number];

/**
 * Riaprendo una conversazione, una riga fatta e poi annullata: il server la segna `annullata`
 * (integrazione 2D, 29/10/2026) e la sua frase comincia con «Annullat». Una carta salvata prima
 * non ha il campo: resta la frase. Senza nessuno dei due la riga resta «Fatto», e un «Annulla
 * questa modifica» su una riga già annullata riceve il 409 e si sistema da sola (annulloDaErrore).
 */
function annullataDalServer(r: RigaEsito): boolean {
  return r.annullata === true || /^\s*annullat/i.test(r.frase);
}

/** Se il server dice che una riga fatta non si annulla più (`annullabile: false`), il tasto non c'è. */
function annullabileDalServer(r: RigaEsito): boolean {
  return r.annullabile !== false;
}

/** Una riga della carta come si disegna adesso, al computer e al telefono. */
export interface VistaRiga {
  riga: RigaCarta;
  /** A sinistra: la spunta (da decidere) o il segno di com'è andata. */
  segno: 'spunta' | 'fatta' | 'annullata' | 'no' | 'vuoto';
  spuntata: boolean;
  /** In grigio: senza spunta, non fatta, annullata, o la carta non vale più. */
  spenta: boolean;
  /** Prima di decidere: «Andrea riceve una mail…» e la descrizione intera che parte per mail. */
  avvisi: string[];
  mail: string | null;
  /** Com'è andata: «Fatto», «Annullato», la frase del server per una riga non fatta. */
  esito: string | null;
  tono: 'mute' | 'alert' | null;
  avvisati: string | null;
  dettaglioAvvisi: string | null;
  /** Perché una riga fatta non si annulla più: dal 409, o dal server riaprendo la conversazione. */
  motivo: string | null;
  /** L'azione figlia da annullare con «Annulla questa modifica», se si può ancora. */
  annulla: string | null;
}

function vistaRighe(c: CartaConferma, fase: FaseCarta, tolte: readonly number[], annulli: Record<string, Annullo>): VistaRiga[] {
  return c.righe.map((riga): VistaRiga => {
    const base: VistaRiga = {
      riga, segno: 'vuoto', spuntata: false, spenta: true, avvisi: [], mail: null,
      esito: null, tono: null, avvisati: null, dettaglioAvvisi: null, motivo: null, annulla: null,
    };
    if (fase === 'aperta') {
      const spuntata = !tolte.includes(riga.indice);
      return { ...base, segno: 'spunta', spuntata, spenta: !spuntata, avvisi: riga.avvisi, mail: riga.descrizioneMail };
    }
    if (fase !== 'fatta') return base;
    // Confermata ma ancora senza esito: le righe restano grigie e senza esito, non «Non fatta».
    if (!c.esito) return base;
    const e = c.esito.righe.find((x) => x.indice === riga.indice);
    // Senza spunta: il server non l'ha nemmeno guardata.
    if (!e) return { ...base, esito: "Non fatta: l'hai lasciata com'era", tono: 'mute' };
    if (e.esito !== 'fatta') {
      // La X rossa solo per un errore (integrazione, 29/10/2026): una riga lasciata senza spunta, o
      // saltata perché qualcuno l'ha cambiata nel frattempo, non è andata storta; lo dice la frase.
      const errore = e.esito === 'errore';
      return { ...base, segno: errore ? 'no' : 'vuoto', esito: e.frase || 'Non fatta', tono: errore ? 'alert' : 'mute' };
    }
    const annullo = e.azioneId ? annulli[e.azioneId] : undefined;
    if (annullo?.esito === 'ANNULLATA' || annullo?.esito === 'PARZIALE' || annullataDalServer(e)) {
      // La frase dell'Annulla del server quando c'è (29/10/2026, dalla revisione della 2D): dice
      // cosa è tornato e cosa resta; e restano scritti gli avvisati, che non tornano indietro.
      const frase = annullo?.frase || 'Annullato';
      const avvisati = avvisatiDopo(frase, e.avvisi);
      return { ...base, segno: 'annullata', esito: frase, tono: 'mute', avvisati, dettaglioAvvisi: avvisati ? dettaglioAvvisi(e.avvisi) : null };
    }
    const nonPiu = !annullabileDalServer(e) && e.perche ? e.perche : null;
    return {
      ...base,
      segno: 'fatta',
      spenta: false,
      esito: 'Fatto',
      tono: 'mute',
      avvisati: fraseAvvisati(e.avvisi),
      dettaglioAvvisi: dettaglioAvvisi(e.avvisi),
      // Perché non si annulla più: dal 409 del clic, o dal server riaprendo la conversazione.
      motivo: annullo?.esito === 'NIENTE' ? annullo.frase : nonPiu,
      annulla: e.azioneId && !annullo && annullabileDalServer(e) ? e.azioneId : null,
    };
  });
}

// ── I clic ───────────────────────────────────────────────────────────────────

/**
 * Un errore della conferma o del rifiuto. Scaduta: la carta diventa scaduta. Già usata (un
 * altro clic, il telefono), di un'altra persona, non trovata: niente più tasti, la frase del
 * server, e per «già usata» si rilegge com'è finita davvero. Il resto (Solo lettura, MUNE
 * spento, la rete): la carta resta da decidere e la frase sta sotto i tasti, si può riprovare.
 */
function erroreDellaCarta(id: string, e: unknown) {
  const st = useCarteStore.getState();
  if (e instanceof ApiError && e.status === 409 && /scadut/i.test(e.message)) {
    aggiornaCarta(id, (c) => ({ ...c, stato: 'SCADUTA' }));
    return;
  }
  if (e instanceof ApiError && e.status === 409 && /già (stata )?usata/i.test(e.message)) {
    st.chiudi(id, e.message);
    void rileggiCarta(id);
    return;
  }
  if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
    st.chiudi(id, e.message);
    return;
  }
  st.problema(id, fraseErrore(e));
}

async function faiConferma(qc: QueryClient, carta: CartaConferma, scelte: number[]) {
  if (!scelte.length || !useCarteStore.getState().inizia(carta.id, 'conferma')) return;
  try {
    const esito = await confermaCarta(carta.id, scelte);
    aggiornaCarta(carta.id, (c) => ({ ...c, stato: 'ESEGUITA', esito }));
    // Le task appena messe nel cestino: le loro righe col link escono dalla chat (dalla revisione
    // delle correzioni, 29/10/2026), come quelle delle task create e poi annullate.
    togliRigheTask((esito.righe ?? []).flatMap((r) => (r.esito === 'fatta' && r.taskNelCestino ? [r.taskNelCestino] : [])));
    // Come per un'azione fatta subito: il fendente e l'inchino (decisione 17).
    if (esito.righe?.some((r) => r.esito === 'fatta')) festeggiaAzione();
  } catch (e) {
    erroreDellaCarta(carta.id, e);
  } finally {
    useCarteStore.getState().finisci(carta.id);
    // Anche dopo un errore: una conferma fermata a metà può aver già scritto qualcosa.
    rinfrescaDopoModifiche(qc);
  }
}

async function faiRifiuto(carta: CartaConferma) {
  if (!useCarteStore.getState().inizia(carta.id, 'rifiuta')) return;
  try {
    await rifiutaCarta(carta.id);
    aggiornaCarta(carta.id, (c) => ({ ...c, stato: 'RIFIUTATA' }));
  } catch (e) {
    erroreDellaCarta(carta.id, e);
  } finally {
    useCarteStore.getState().finisci(carta.id);
  }
}

/**
 * Un Annulla che il server rifiuta col 409 dice perché («Non si annulla più: …»): la riga lo
 * scrive e il tasto sparisce. «Già stata annullata» (dal registro, dall'altra scheda) vuol dire
 * che è già com'era: la riga diventa «Annullato». 403 e 404: non si annulla da qui. Per il
 * resto (la rete, il server fermo) null: si può riprovare.
 */
function annulloDaErrore(e: unknown): Annullo | null {
  if (!(e instanceof ApiError)) return null;
  if (e.status === 409) return /già stata annullata/i.test(e.message) ? { esito: 'ANNULLATA', frase: '' } : { esito: 'NIENTE', frase: e.message };
  if (e.status === 403 || e.status === 404) return { esito: 'NIENTE', frase: e.message };
  return null;
}

/** Segna l'Annulla di un'azione: nello store e, se è un'azione della chat, nella sua parte. */
function segnaAnnullo(id: string, a: Annullo) {
  useCarteStore.getState().annullata(id, a);
  aggiornaAzione(id, (x) => (a.esito === 'NIENTE' ? { ...x, annullabile: false } : { ...x, stato: 'ANNULLATA', annullabile: false }));
}

/** Le righe con link di una risposta, senza le task tornate nel cestino. */
export function senzaTaskCestinate(righe: RigaLink[]): RigaLink[] {
  const cestinate = useCarteStore.getState().cestinate;
  return righe.filter((x) => !(x.tipo === 'task' && cestinate[x.id]));
}

/**
 * Le task appena finite nel cestino (29/10/2026, dalla revisione della 2D): le loro righe col link
 * escono dalle risposte della chat, che le aprirebbero su «Task non trovato». Quelle che arrivano
 * dopo, a fine turno, le filtra il pannello (senzaTaskCestinate). Una parte di righe rimasta vuota
 * esce: sta sempre in fondo alla risposta.
 */
function togliRigheTask(ids: readonly string[]) {
  if (!ids.length) return;
  useCarteStore.getState().cestina(ids);
  const st = useAssistenteStore.getState();
  for (const m of st.messaggi) {
    const parti = m.parti;
    if (!parti?.some((p) => p.tipo === 'righe' && p.righe.some((x) => x.tipo === 'task' && ids.includes(x.id)))) continue;
    st.aggiornaMessaggio(m.id, {
      parti: parti.flatMap((p): ParteRisposta[] => {
        if (p.tipo !== 'righe') return [p];
        const righe = senzaTaskCestinate(p.righe);
        return righe.length ? [{ tipo: 'righe', righe }] : [];
      }),
    });
  }
}

/**
 * Dopo un Annulla riuscito, le task che l'azione aveva creato tornano nel cestino e le loro righe
 * escono (togliRigheTask). Quelle che aveva eliminato tornano fuori dal cestino (dalla revisione
 * delle correzioni, 29/10/2026): le righe che arrivano dopo le mostrano di nuovo. Quelle già tolte
 * da questa chat tornano riaprendo la conversazione.
 */
function togliTaskCestinate(esito: EsitoAnnulla) {
  const rimesse = (esito.righe ?? []).filter((r) => r.esito === 'rimessa');
  const tornate = rimesse.filter((r) => r.cosa === 'eliminata').map((r) => r.id);
  if (tornate.length) useCarteStore.getState().ripesca(tornate);
  togliRigheTask(rimesse.filter((r) => r.cosa === 'creata').map((r) => r.id));
}

/**
 * Un Annulla riuscito, da qui o da «Cosa ha fatto MUNE» (29/10/2026, dalla revisione della 2D):
 * l'azione nella chat, o la riga della carta, dice subito «Annullato» con la frase del server, e
 * le righe col link delle task tornate nel cestino escono. Prima il registro aggiornava solo le
 * sue query, e la chat restava su «Fatto … · Annulla» fino a un 409.
 */
export function dopoAnnullo(id: string, esito: EsitoAnnulla) {
  segnaAnnullo(id, { esito: esito.stato === 'PARZIALE' ? 'PARZIALE' : 'ANNULLATA', frase: testo(esito.frase) });
  togliTaskCestinate(esito);
}

/**
 * «Annulla» di un'azione fatta subito e «Annulla questa modifica» di una riga della carta: è
 * il gesto appena fatto, quindi niente conferma, come l'Annulla del toast (annullaSubito).
 * L'esito resta scritto nella riga. Si può anche a MUNE spento: l'Annulla non chiama il modello.
 */
async function faiAnnullo(qc: QueryClient, id: string) {
  if (!useCarteStore.getState().inizia(id, 'annulla')) return;
  try {
    const esito = await annullaAzione(id);
    dopoAnnullo(id, esito);
  } catch (e) {
    const a = annulloDaErrore(e);
    if (a) segnaAnnullo(id, a);
    else toast.alert('Annullamento non riuscito', fraseErrore(e));
  } finally {
    useCarteStore.getState().finisci(id);
    rinfrescaDopoModifiche(qc);
  }
}

/**
 * «Annulla tutto»: annulla la carta, cioè tutte le figlie ancora come le ha lasciate MUNE
 * (decisione 15). Le righe che diventano «Annullato» sono quelle delle azioni che il server dice
 * in `esito.azioni` (integrazione 2D, 29/10/2026): una figlia che non è tornata tiene il suo
 * Annulla. Un server senza il campo: tutte le righe fatte, se è riuscito per intero. In parte,
 * la carta si rilegge anche dalla conversazione, che sa quali righe si annullano ancora.
 */
async function faiAnnullaTutto(qc: QueryClient, carta: CartaConferma) {
  if (!useCarteStore.getState().inizia(carta.id, 'annulla')) return;
  try {
    const esito = await annullaAzione(carta.id);
    const tutto = esito.stato !== 'PARZIALE';
    useCarteStore.getState().annullata(carta.id, { esito: tutto ? 'ANNULLATA' : 'PARZIALE', frase: testo(esito.frase) });
    togliTaskCestinate(esito);
    const tornate = Array.isArray(esito.azioni) ? new Set(esito.azioni) : null;
    for (const r of carta.esito?.righe ?? []) {
      if (r.esito !== 'fatta' || !r.azioneId || useCarteStore.getState().annulli[r.azioneId]) continue;
      if (tornate ? tornate.has(r.azioneId) : tutto) segnaAnnullo(r.azioneId, { esito: 'ANNULLATA', frase: '' });
    }
    if (!tutto) void rileggiCarta(carta.id);
  } catch (e) {
    const a = annulloDaErrore(e);
    if (a) {
      useCarteStore.getState().annullata(carta.id, a);
      void rileggiCarta(carta.id);
    } else {
      toast.alert('Annullamento non riuscito', fraseErrore(e));
    }
  } finally {
    useCarteStore.getState().finisci(carta.id);
    rinfrescaDopoModifiche(qc);
  }
}

// ── Gli hook dei componenti ──────────────────────────────────────────────────

/**
 * Vero quando l'ora di `scade` è passata. Ridisegna da solo a quell'ora, così la carta diventa
 * «Scaduta: chiedimelo di nuovo» e la barra del telefono sparisce senza aspettare un clic.
 * Con una scadenza che non si legge non scade mai qui: lo dirà il server.
 */
export function useScaduta(scade: string | null): boolean {
  const limite = scade ? Date.parse(scade) : Number.NaN;
  const [, ridisegna] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(limite)) return;
    const resta = limite - Date.now();
    if (resta <= 0) return;
    // Un quarto di secondo dopo: l'orologio del timer può arrivare un filo prima.
    const t = window.setTimeout(() => ridisegna((n) => n + 1), Math.min(resta + 250, 2_000_000_000));
    return () => window.clearTimeout(t);
  }, [limite]);
  return Number.isFinite(limite) && Date.now() >= limite;
}

/**
 * Com'è la carta adesso, senza React: la fase, la riga sotto il titolo, le righe, le scelte e il
 * tasto. La usa useCarta; sta a parte perché si prova con uno script, senza browser.
 */
export function vistaCarta(c: CartaConferma, s: {
  tolte: readonly number[];
  annulli: Record<string, Annullo>;
  chiusa: string | null;
  scaduta: boolean;
}) {
  const fase = faseDi(c, s.chiusa, s.scaduta);
  const righe = vistaRighe(c, fase, s.tolte, s.annulli);
  const scelte = c.righe.map((r) => r.indice).filter((i) => !s.tolte.includes(i));
  const annulloCarta = s.annulli[c.id] ?? null;
  return {
    fase,
    /** «Modifiche da confermare: 2 task di Graci» finché si decide, poi «Modifiche fatte: …» e gli altri. */
    titolo: titoloDellaCarta(c, fase, righe),
    /** La riga sotto il titolo: «Vale fino alle 11:12», «Scaduta: chiedimelo di nuovo», l'esito. */
    frase: fraseDellaCarta(c, fase, s.chiusa),
    righe,
    scelte,
    tasto: testoTasto(c, scelte),
    /** Il tasto pieno cancella e basta: rosso. */
    tastoElimina: soloEliminazioni(c, scelte),
    annulloCarta,
    /** «Annulla tutto» solo quando c'è più di una riga da annullare: con una sola basta la sua. */
    conAnnullaTutto: fase === 'fatta' && !annulloCarta && righe.filter((v) => v.annulla).length > 1,
  };
}

/** Tutto quello che serve a disegnare una carta e ai suoi tasti, nella chat o nella barra del telefono. */
export function useCarta(grezza: CartaConferma) {
  const qc = useQueryClient();
  const c = useMemo(() => cartaCompleta(grezza), [grezza]);
  const tolte = useCarteStore((s) => s.tolte[c.id] ?? NESSUNA);
  const lavori = useCarteStore((s) => s.lavori);
  const problema = useCarteStore((s) => s.problemi[c.id] ?? null);
  const chiusa = useCarteStore((s) => s.chiuse[c.id] ?? null);
  const annulli = useCarteStore((s) => s.annulli);
  const scaduta = useScaduta(c.stato === 'PROPOSTA' ? c.scade : null);

  const vista = vistaCarta(c, { tolte, annulli, chiusa, scaduta });
  const { fase, righe, scelte } = vista;
  const lavoro = lavori[c.id] ?? null;
  // Un clic alla volta su tutta la carta: la conferma, il rifiuto, l'Annulla di una riga o di tutto.
  const occupata = !!lavoro || righe.some((v) => !!v.annulla && !!lavori[v.annulla]);

  return {
    c,
    ...vista,
    lavoro,
    occupata,
    problema: fase === 'aperta' ? problema : null,
    puoConfermare: fase === 'aperta' && !occupata && scelte.length > 0,
    lavoroDi: (id: string): Lavoro | null => lavori[id] ?? null,
    spunta: (indice: number, on: boolean) => useCarteStore.getState().spunta(c.id, indice, on),
    conferma: () => { void faiConferma(qc, c, scelte); },
    rifiuta: () => { void faiRifiuto(c); },
    annullaRiga: (azioneId: string) => { void faiAnnullo(qc, azioneId); },
    annullaTutto: () => { void faiAnnullaTutto(qc, c); },
  };
}

export type UsoCarta = ReturnType<typeof useCarta>;

/** La riga di un'azione fatta subito: la frase di adesso, chi è stato avvisato, l'Annulla. */
export function useAzione(grezza: AzioneInChat) {
  const qc = useQueryClient();
  const a = useMemo(() => azioneCompleta(grezza), [grezza]);
  const annullo = useCarteStore((s) => s.annulli[a.id] ?? null);
  const lavoro = useCarteStore((s) => s.lavori[a.id] ?? null);
  const annullata = a.stato === 'ANNULLATA' || annullo?.esito === 'ANNULLATA' || annullo?.esito === 'PARZIALE';
  const frase = fraseAzione(a, annullo);
  // Annullata, la riga dice ancora chi è stato avvisato: mail e messaggi già partiti restano.
  const avvisati = annullata ? avvisatiDopo(frase, a.avvisi) : fraseAvvisati(a.avvisi);
  return {
    a,
    frase,
    annullata,
    avvisati,
    dettaglioAvvisi: dettaglioAvvisi(a.avvisi),
    // Perché non si annulla più: dal 409 del clic, o dal server riaprendo la conversazione.
    motivo: annullo?.esito === 'NIENTE' ? annullo.frase : !annullata && !a.annullabile ? a.perche ?? null : null,
    puoAnnullare: !annullata && a.annullabile && !annullo,
    annullando: lavoro === 'annulla',
    annulla: () => { void faiAnnullo(qc, a.id); },
  };
}

// ── Il pannello ──────────────────────────────────────────────────────────────

/** L'ultima carta dell'ultima risposta, in qualunque stato: al telefono i suoi tasti stanno nella barra. */
export function cartaDellUltimaRisposta(messaggi: MessaggioChat[]): CartaConferma | null {
  const m = messaggi[messaggi.length - 1];
  if (!m || m.ruolo !== 'assistente') return null;
  const parti = m.parti ?? [];
  for (let i = parti.length - 1; i >= 0; i--) {
    const p = parti[i];
    if (p?.tipo === 'carta') return p.carta;
  }
  return null;
}

/**
 * La carta da decidere nella barra del telefono, che prende il posto del campo: solo se è
 * ancora da decidere, non è scaduta e il server non l'ha chiusa, e solo quando `pronta` (il
 * turno è finito e la carta si vede). Null se la barra non serve.
 */
export function useCartaInBarra(carta: CartaConferma | null, pronta: boolean): CartaConferma | null {
  const daDecidere = carta && carta.stato === 'PROPOSTA' ? carta : null;
  const scaduta = useScaduta(daDecidere?.scade ?? null);
  const chiusa = useCarteStore((s) => (daDecidere ? s.chiuse[daDecidere.id] ?? null : null));
  return pronta && daDecidere && !scaduta && !chiusa ? daDecidere : null;
}

/**
 * Quello che si legge di una risposta a fine turno, per i lettori di schermo: come
 * testoDaLeggere (chatApi.ts), più la frase di un'azione fatta e il titolo di una carta.
 */
export function testoDaLeggereConModifiche(parti: ParteRisposta[]): string {
  return parti
    .map((p) => {
      if (p.tipo === 'azione') return fraseAzione(azioneCompleta(p.azione), null);
      if (p.tipo === 'carta') {
        // A fine turno la carta è sempre da decidere: il titolo del server, «Modifiche da confermare: …».
        const c = cartaCompleta(p.carta);
        return c.titolo ? `${c.titolo}.` : '';
      }
      return testoDaLeggere([p]);
    })
    .filter(Boolean)
    .join(' ');
}
